import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 75. EXEMPLE DE RELATION GLOBALE
// Un client ne doit pas être dupliqué dans chaque module :
//
//   USER → CUSTOMER PROFILE → BUSINESS RELATIONSHIP
//                    │
//   CUSTOMER ├── Appointment ├── Booking ├── Order ├── Invoice
//            ├── Payment     ├── Review  ├── Favorite ├── Document
//            └── Conversation
//
// Ce service construit le CONTEXTE GLOBAL d'un client en agrégeant les
// entités réelles de chaque module, SANS recopier les données. Zifek AI peut
// ainsi comprendre le contexte complet du client en un seul appel.
// ─────────────────────────────────────────────────────────────

export interface CustomerRelationItem {
  id: string;
  title: string;
  subtitle?: string | null;
  amount?: number | null;
  currency?: string | null;
  status?: string | null;
  date?: string | null;
}

export interface CustomerRelationGroup {
  key: string;
  label: string;
  icon: string;
  entityType: string;
  items: CustomerRelationItem[];
}

export interface CustomerProfile {
  id: number;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  customerType: string | null;
  companyName: string | null;
  userId: number | null;
  status: string | null;
}

export interface CustomerGlobalContext {
  customer: CustomerProfile | null;
  groups: CustomerRelationGroup[];
  totals: {
    relations: number;
    categories: number;
  };
}

const GROUP_DEFS: { key: string; label: string; icon: string; entityType: string }[] = [
  { key: 'appointments', label: 'Rendez-vous', icon: 'ri-calendar-check-line', entityType: 'appointment' },
  { key: 'bookings', label: 'Réservations', icon: 'ri-hotel-line', entityType: 'booking' },
  { key: 'orders', label: 'Commandes', icon: 'ri-shopping-bag-3-line', entityType: 'order' },
  { key: 'invoices', label: 'Factures', icon: 'ri-file-list-3-line', entityType: 'invoice' },
  { key: 'payments', label: 'Paiements', icon: 'ri-money-dollar-circle-line', entityType: 'payment' },
  { key: 'reviews', label: 'Avis', icon: 'ri-chat-quote-line', entityType: 'review' },
  { key: 'favorites', label: 'Favoris', icon: 'ri-bookmark-3-line', entityType: 'favorite' },
  { key: 'documents', label: 'Documents', icon: 'ri-file-text-line', entityType: 'document' },
  { key: 'conversations', label: 'Conversations', icon: 'ri-chat-3-line', entityType: 'conversation' },
];

function emptyGroups(): CustomerRelationGroup[] {
  return GROUP_DEFS.map((g) => ({ ...g, items: [] }));
}

function groupOf(groups: CustomerRelationGroup[], key: string): CustomerRelationGroup {
  return groups.find((g) => g.key === key) || groups[0];
}

/** Construit le contexte global d'un client à partir de TOUTES ses relations réelles. */
export async function resolveCustomerContext(params: {
  organizationId: number;
  commerceId?: number | null;
  customerId: number;
}): Promise<CustomerGlobalContext> {
  const groups = emptyGroups();

  const { data: cust } = await supabase
    .from('core_customers')
    .select('id, display_name, email, phone, customer_type, company_name, user_id, status')
    .eq('id', params.customerId)
    .maybeSingle();

  const c = cust as Record<string, unknown> | null;
  const customer: CustomerProfile | null = c
    ? {
        id: Number(c.id),
        displayName: (c.display_name as string) ?? null,
        email: (c.email as string) ?? null,
        phone: (c.phone as string) ?? null,
        customerType: (c.customer_type as string) ?? null,
        companyName: (c.company_name as string) ?? null,
        userId: c.user_id != null ? Number(c.user_id) : null,
        status: (c.status as string) ?? null,
      }
    : null;

  const email = customer?.email || null;
  const phone = customer?.phone || null;
  const name = customer?.displayName || null;

  // ── Rendez-vous (par email / téléphone / nom) ──
  try {
    let query = supabase.from('booking_appointments').select('id, fullname, date, time, status').limit(50);
    if (email) query = query.ilike('email', email);
    else if (phone) query = query.eq('phone', phone);
    else if (name) query = query.ilike('fullname', `%${name}%`);
    else query = query.eq('id', -1);
    const { data } = await query;
    groupOf(groups, 'appointments').items = (data || []).map((r) => {
      const row = r as Record<string, unknown>;
      return {
        id: String(row.id),
        title: (row.fullname as string) || 'Rendez-vous',
        subtitle: [row.date, row.time].filter(Boolean).join(' · ') || null,
        status: (row.status as string) ?? null,
        date: (row.date as string) ?? null,
      };
    });
  } catch { /* best-effort */ }

  // ── Réservations (par customer_id) ──
  try {
    const { data } = await supabase
      .from('hebergement_reservations')
      .select('id, guest_name, check_in, check_out, nights, status')
      .eq('customer_id', params.customerId)
      .order('check_in', { ascending: false })
      .limit(50);
    groupOf(groups, 'bookings').items = (data || []).map((r) => {
      const row = r as Record<string, unknown>;
      return {
        id: String(row.id),
        title: (row.guest_name as string) || 'Réservation',
        subtitle: [row.check_in, row.check_out].filter(Boolean).join(' → ') || null,
        status: (row.status as string) ?? null,
        date: (row.check_in as string) ?? null,
      };
    });
  } catch { /* best-effort */ }

  // ── Commandes (par core_customer_id) ──
  try {
    const { data } = await supabase
      .from('commande')
      .select('id, nomclient, nom, totalttc, monaie, statut, created_at')
      .eq('core_customer_id', params.customerId)
      .order('created_at', { ascending: false })
      .limit(50);
    groupOf(groups, 'orders').items = (data || []).map((r) => {
      const row = r as Record<string, unknown>;
      return {
        id: String(row.id),
        title: `Commande #${row.id}`,
        subtitle: (row.nomclient as string) || (row.nom as string) || null,
        amount: row.totalttc != null ? Number(row.totalttc) : null,
        currency: (row.monaie as string) ?? null,
        status: (row.statut as string) ?? null,
        date: (row.created_at as string) ?? null,
      };
    });
  } catch { /* best-effort */ }

  // ── Avis (par author_user_id) ──
  if (customer?.userId) {
    try {
      const { data } = await supabase
        .from('core_reviews')
        .select('id, rating, title, entity_type, entity_id')
        .eq('author_user_id', customer.userId)
        .limit(50);
      groupOf(groups, 'reviews').items = (data || []).map((r) => {
        const row = r as Record<string, unknown>;
        return {
          id: String(row.id),
          title: (row.title as string) || 'Avis',
          subtitle: `${'★'.repeat(Number(row.rating) || 0)}${'☆'.repeat(Math.max(0, 5 - (Number(row.rating) || 0)))}`,
          status: `${row.entity_type || ''}`.trim() || null,
        };
      });
    } catch { /* best-effort */ }

    // ── Favoris (par user_id) ──
    try {
      const { data } = await supabase
        .from('core_favorites')
        .select('id, entity_type, entity_id, label, created_at')
        .eq('user_id', customer.userId)
        .order('created_at', { ascending: false })
        .limit(50);
      groupOf(groups, 'favorites').items = (data || []).map((r) => {
        const row = r as Record<string, unknown>;
        return {
          id: String(row.id),
          title: (row.label as string) || `${row.entity_type} #${row.entity_id}`,
          subtitle: (row.entity_type as string) ?? null,
          date: (row.created_at as string) ?? null,
        };
      });
    } catch { /* best-effort */ }
  }

  // ── Documents (entity_type = customer) ──
  try {
    const { data } = await supabase
      .from('documents')
      .select('id, name, doc_type, created_at')
      .eq('entity_type', 'customer')
      .eq('entity_id', String(params.customerId))
      .limit(50);
    groupOf(groups, 'documents').items = (data || []).map((r) => {
      const row = r as Record<string, unknown>;
      return {
        id: String(row.id),
        title: (row.name as string) || 'Document',
        subtitle: (row.doc_type as string) ?? null,
        date: (row.created_at as string) ?? null,
      };
    });
  } catch { /* best-effort */ }

  // ── Conversations (par customer_id) ──
  try {
    const { data } = await supabase
      .from('core_conversations')
      .select('id, subject, channel, status, last_message_at')
      .eq('customer_id', params.customerId)
      .order('last_message_at', { ascending: false })
      .limit(50);
    groupOf(groups, 'conversations').items = (data || []).map((r) => {
      const row = r as Record<string, unknown>;
      return {
        id: String(row.id),
        title: (row.subject as string) || 'Conversation',
        subtitle: (row.channel as string) ?? null,
        status: (row.status as string) ?? null,
        date: (row.last_message_at as string) ?? null,
      };
    });
  } catch { /* best-effort */ }

  const relations = groups.reduce((sum, g) => sum + g.items.length, 0);
  const categories = groups.filter((g) => g.items.length > 0).length;
  return { customer, groups, totals: { relations, categories } };
}

/** Contexte global à partir d'un compte Zifek (résout la fiche client). */
export async function resolveCustomerContextByUser(params: {
  organizationId: number;
  commerceId?: number | null;
  userId: number;
}): Promise<CustomerGlobalContext> {
  const { data } = await supabase
    .from('core_customers')
    .select('id')
    .eq('organization_id', params.organizationId)
    .eq('user_id', params.userId)
    .maybeSingle();
  const id = (data?.id as number) ?? null;
  if (!id) {
    return { customer: null, groups: emptyGroups(), totals: { relations: 0, categories: 0 } };
  }
  return resolveCustomerContext({ ...params, customerId: id });
}

/** Chaîne des relations d'un client pour l'affichage (sans duplication). */
export const CUSTOMER_RELATION_CHAIN: { key: string; label: string; icon: string }[] = [
  { key: 'user', label: 'User', icon: 'ri-user-3-line' },
  { key: 'customer', label: 'Customer profile', icon: 'ri-user-star-line' },
  { key: 'business', label: 'Business relationship', icon: 'ri-building-2-line' },
];